import { useCallback } from 'react';

import { type DietaryRestriction } from '@sg/graphql-schema';
import { useDietaryPreferencesMachine as useSharedDietaryPreferencesMachine } from '@sg/shared-fe/features/menu';

import { useTelemetry } from '@order/Telemetry';

import { useGlobalAppState } from '../GlobalAppState';

// ─────────────────────────────────────────────────────────────────────────────

export const useDietaryPreferencesMachine = (params?: Params) => {
  const { onToggleRestriction, referrer } = params ?? {};

  const { dietaryRestrictionsMachineRef: actor } = useGlobalAppState();
  const { track } = useTelemetry();

  const {
    preferences,
    pendingPreferences,
    sortedPreferencesNames,
    isIdle,
    isActive,
    isUpdatingRemotePreferences,
    isFetchingCustomerPreferences,
    hasUnsavedPreferences,
    hasSelectedPreferences,
    start,
    togglePreference,
    confirmChanges,
    cancelChanges,
    resetPreferences,
    fetchCustomerPreferences,
  } = useSharedDietaryPreferencesMachine({ actor });

  // ─── Telemetry-Wrapped Toggle ──────────────────────────────────────

  const toggleRestriction = useCallback(
    (value: DietaryRestriction) => {
      const selected = !pendingPreferences[value];

      track('dietary-restrictions.select', {
        route: referrer,
        restriction: value,
        selected,
      });

      togglePreference(value);
      onToggleRestriction?.(value);
    },
    [pendingPreferences, togglePreference, onToggleRestriction, referrer, track],
  );

  // ─── Aliases for backwards-compatible naming ───────────────────────

  return {
    restrictions: preferences,
    pendingRestrictions: pendingPreferences,
    sortedRestrictionsNames: sortedPreferencesNames,
    isIdle,
    isActive,
    isUpdatingRemoteRestrictions: isUpdatingRemotePreferences,
    isFetchingCustomerPreferences,
    hasUnsavedPreferences,
    hasSelectedRestrictions: hasSelectedPreferences,
    start,
    toggleRestriction,
    confirmChanges,
    cancelChanges,
    resetRestrictions: resetPreferences,
    fetchCustomerPreferences,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  onToggleRestriction?: (value: DietaryRestriction) => void;
  referrer?: string;
};
